<template>
  <div class="stage-level-detail px-2 py-1">
    <v-card class="caption text--secondary mb-1" flat height="20">
      {{ selectedObservatory.address }}
    </v-card>
    <div class="caption ml-2" v-if="isChartVisible">（{{ levelUnit }}）</div>
    <StageChart
      v-if="isChartVisible"
      :height="isMobile ? 160 : 240"
      :selected-observatory="selectedObservatory"
      :displayHours="displayHours"
      :chartTimeSeries="chartTimeSeries"
      :mesuredLatestValue="mesuredLatestValue"
      :levelUnit="levelUnit"
      :chartWaterBoxLevel="chartWaterBoxLevel"
    />
    <v-card
      class="d-flex align-center justify-end mt-2"
      flat
      v-if="currentLevel !== null"
    >
      <v-card width="30" height="22" color="#3D8AFF"></v-card>
      <div class="caption ml-4">
        現在水位 {{ currentLevel }} {{ levelUnit }}
      </div>
    </v-card>
    <div class="mt-1" v-if="isChartVisible && !isBraveridgeWaterway">
      <div class="caption">基準水位</div>
      <div
        class="caption d-flex"
        v-for="std in standardLevels"
        :key="std.label"
        style="line-height: 1.4"
      >
        <v-card width="70" flat tile>
          <div
            class="std-color ml-8"
            :style="{ 'border-bottom-color': std.color }"
          ></div>
        </v-card>
        <v-card width="100" flat tile>{{ std.label }}</v-card>
        <v-card width="100" flat tile class="text-right">
          {{
            std.level == undefined
              ? "---"
              : std.level.toFixed(2) + " " + levelUnit
          }}
        </v-card>
      </div>
    </div>

    <v-simple-table
      dense
      class="mt-2"
      v-if="!isDeficit"
      fixed-header
      :height="
        isOverflowWaterway ? 'calc(100dvh - 370px)' : 'calc(100dvh - 480px)'
      "
    >
      <template v-slot:default>
        <thead>
          <tr>
            <th class="text-left">日時</th>
            <th class="text-left">水位 ({{ levelUnit }})</th>
            <th style="width: 100px"></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(ts, index) in tableTimeSeries"
            :key="index"
            :class="{ predict: ts.predict }"
            :id="`row-${index}`"
          >
            <td>{{ ts.time }}</td>
            <td class="text-right">{{ ts.level }}</td>
            <td></td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>
    <div class="mt-5 caption" v-if="isDeficit">
      <template
        v-if="selectedObservatory.kiki === true && chartWaterBoxLevel !== null"
      >
        この観測所は観測開始水位以下です。
      </template>
      <template v-else>
        この観測所は欠測しています。
      </template>
    </div>
  </div>
</template>

<script>
import StageChart from "./StageChart";
import { OBSERVATORY, STAGES } from "../../../enums/Type";
import moment from "moment";

export default {
  name: "StageDetail",
  components: {
    StageChart
  },
  data() {
    return {
      displayHours: 1
    };
  },
  computed: {
    isOverflowWaterway() {
      return (
        this.$store.getters.selectedObservatory.type ===
        OBSERVATORY.OVERFLOW_WATERWAY.code
      );
    },
    isMobile() {
      return this.$route.path === "/mobile";
    },
    selectedObservatory() {
      return this.$store.getters.selectedObservatory;
    },
    isBraveridgeWaterway() {
      return (
        this.$store.getters.selectedObservatory.type ===
        OBSERVATORY.OVERFLOW_WATERWAY.code
      );
    },
    levelUnit() {
      if (this.isBraveridgeWaterway === true) {
        return "cm";
      }
      return this.selectedObservatory.kind === 9 ? "TP.m" : "m";
    },
    mesuredData() {
      if (this.selectedObservatory.timeSeries === undefined) {
        return [];
      }
      return this.selectedObservatory.timeSeries.filter(v => v.type === "real");
    },
    predictData() {
      if (this.selectedObservatory.timeSeries === undefined) {
        return [];
      }
      return this.selectedObservatory.timeSeries.filter(
        v => v.type === "forecast"
      );
    },
    mesuredLatestValue() {
      const date = this.baseDate.clone();
      return date.format("YYYY/MM/DD HH:mm");
    },
    currentLevel() {
      const levels = this.mesuredData
        .map(r => r.level)
        .filter(l => l !== undefined);
      if (levels.length === 0) {
        if (
          this.selectedObservatory.kiki === true &&
          this.chartWaterBoxLevel !== null
        ) {
          return this.chartWaterBoxLevel.toFixed(2);
        } else {
          return null;
        }
      } else {
        return levels[levels.length - 1].toFixed(2);
      }
    },
    baseDate() {
      return this.$store.getters.baseDate;
    },
    chartTimeSeries() {
      if (this.mesuredLatestValue === null) {
        return [];
      }
      const result = [];
      result.push(this.mesuredLatestValue);
      const latestMoment = moment.utc(
        this.mesuredLatestValue,
        "YYYY/MM/DD HH:mm"
      );
      [...Array(180).keys()].forEach(i => {
        const d = latestMoment.clone().add(-1 * (i + 1), "minutes");
        result.unshift(d.format("YYYY/MM/DD HH:mm"));
      });
      [...Array(this.displayHours * 60).keys()].forEach(i => {
        const d = latestMoment.clone().add(1 * (i + 1), "minutes");
        result.push(d.format("YYYY/MM/DD HH:mm"));
      });
      return result;
    },
    isDeficit() {
      return this.mesuredData.length === 0 && this.predictData.length === 0;
    },
    standardLevels() {
      const levels = [];

      if (this.selectedObservatory.kiki === true) {
        levels.push({
          label: "氾濫発生水位",
          color: STAGES.OUTBREAK.color,
          level: this.selectedObservatory.floodLevel
        });
        levels.push({
          label: "氾濫危険水位",
          color: STAGES.DANGEROUS.color,
          level: this.selectedObservatory.warnLevel
        });
        levels.push({
          label: "観測開始水位",
          color: STAGES.STANDBY.color,
          level: this.selectedObservatory.startLevel
        });
      } else {
        levels.push({
          label: STAGES.OUTBREAK.label,
          color: STAGES.OUTBREAK.color,
          level: this.selectedObservatory.outbreakLevel
        });
        levels.push({
          label: STAGES.DANGEROUS.label,
          color: STAGES.DANGEROUS.color,
          level: this.selectedObservatory.dangerousLevel
        });
        levels.push({
          label: STAGES.EVACUATION.label,
          color: STAGES.EVACUATION.color,
          level: this.selectedObservatory.evacuationLevel
        });
        levels.push({
          label: STAGES.WARNING.label,
          color: STAGES.WARNING.color,
          level: this.selectedObservatory.warningLevel
        });
        levels.push({
          label: STAGES.STANDBY.label,
          color: STAGES.STANDBY.color,
          level: this.selectedObservatory.standbyLevel
        });
      }
      return levels;
    },
    tableTimeSeries() {
      const timeSeries = [];
      this.chartTimeSeries.forEach(tsVal => {
        const finded =
          tsVal <= this.mesuredLatestValue
            ? this.mesuredData.find(m => m.date === tsVal)
            : this.predictData.find(p => p.date === tsVal);
        if (finded !== undefined) {
          const level =
            finded.level === undefined ? "---" : finded.level.toFixed(2);
          timeSeries.push({
            time: moment
              .utc(tsVal, "YYYY/MM/DD HH:mm")
              .local()
              .format("MM/DD HH:mm"),
            level: level,
            predict: tsVal > this.mesuredLatestValue
          });
        }
      });
      return timeSeries.slice().reverse();
    },
    isDeficitRain() {
      if (this.selectedObservatory.timeSeries === undefined) {
        return true;
      }
      const filtered = this.selectedObservatory.timeSeries.filter(
        v => v.rain !== undefined
      );
      return filtered.length === 0;
    },
    chartWaterBoxLevel() {
      const target = this.$store.getters.observatories.find(
        o =>
          o.id === this.selectedObservatory.id &&
          o.kiki === true &&
          o.chartWaterBoxLevel !== undefined
      );
      return target === undefined ? null : target.chartWaterBoxLevel;
    },
    isChartVisible() {
      return !this.isDeficit || this.chartWaterBoxLevel !== null;
    }
  },
  watch: {
    selectedObservatory() {
      setTimeout(() => {
        this.scrollNow();
      }, 500);
    },
    tableTimeSeries() {
      setTimeout(() => {
        this.scrollNow();
      }, 500);
    }
  },
  mounted() {
    setTimeout(() => {
      this.scrollNow();
    }, 500);
  },
  methods: {
    scrollNow() {
      const targetIndex = this.tableTimeSeries.findIndex(row => !row.predict);
      if (targetIndex === -1) return;
      const id = `#row-${targetIndex}`;
      const height = this.$el.querySelector(id).clientHeight;
      const tableWrapper = this.$el.getElementsByClassName(
        "v-data-table__wrapper"
      )[0];
      let top = 0;
      if (targetIndex > 0) {
        top = height * (targetIndex - (this.isMobile ? 2 : 3));
      }
      tableWrapper.scroll({ top, behavior: "instant" });
    }
  }
};
</script>

<style lang="scss" scoped>
.stage-level-detail {
  thead {
    background-color: #addff3;
  }
  tbody td {
    background: #eee;
  }
  tbody tr:nth-child(odd) td {
    background: #fff;
  }
  .std-color {
    width: 16px;
    height: 12px;
    border-bottom: white 3px solid;
  }
  .predict {
    color: blue;
  }
}
</style>
